/**
 * HIPAA-required session idle timeout.
 *
 * Field tablets are shared between crew members and routinely left
 * unattended on truck dashboards / patient compartments. The Security
 * Rule (45 CFR §164.312(a)(2)(iii)) requires "automatic logoff … that
 * terminates an electronic session after a predetermined time of
 * inactivity." 30 minutes is the de-facto industry standard; we warn
 * at 25 minutes so the user isn't surprised mid-charting.
 *
 * Behavior:
 *   - Activity events (mousemove, keydown, touchstart, click, scroll)
 *     reset the timer.
 *   - At WARN_MS we emit a 'idle:warning' CustomEvent on window so any
 *     UI surface can render a countdown toast.
 *   - At LOGOUT_MS we call signOut() and the user is sent to /login.
 *
 * Disabled automatically in DEV mode (no Supabase env) so local
 * tinkering doesn't keep kicking the developer out.
 */

import { useAuthStore } from './auth-store';

const IDLE_LOGOUT_MS = 30 * 60 * 1000; // 30 minutes
const IDLE_WARN_MS   = 25 * 60 * 1000; // 25 minutes — 5-minute warning window
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'touchstart', 'click', 'scroll'] as const;

let logoutTimer: ReturnType<typeof setTimeout> | null = null;
let warnTimer: ReturnType<typeof setTimeout> | null = null;
let started = false;

function clearTimers() {
  if (logoutTimer) clearTimeout(logoutTimer);
  if (warnTimer) clearTimeout(warnTimer);
  logoutTimer = null;
  warnTimer = null;
  // Cancel any visible warning toast on activity reset
  window.dispatchEvent(new CustomEvent('idle:cleared'));
}

function scheduleTimers() {
  clearTimers();
  warnTimer = setTimeout(() => {
    window.dispatchEvent(
      new CustomEvent('idle:warning', {
        detail: { msUntilLogout: IDLE_LOGOUT_MS - IDLE_WARN_MS },
      }),
    );
  }, IDLE_WARN_MS);

  logoutTimer = setTimeout(() => {
    // signOut clears Supabase session + zustand state → App.tsx routes
    // back to LoginPage on the next render.
    void useAuthStore.getState().signOut();
    window.dispatchEvent(new CustomEvent('idle:logout'));
  }, IDLE_LOGOUT_MS);
}

function onActivity() {
  // Only kick the timer if a user is actually signed in. Otherwise
  // we'd be scheduling logouts against an anonymous session.
  if (!useAuthStore.getState().user) return;
  scheduleTimers();
}

export function startIdleTimer() {
  if (started) return;
  // Dev mode auto-logins with a mock user and has no real session to
  // protect. Disable the timer there so developers aren't ejected
  // from the app every half-hour while reading code.
  if (useAuthStore.getState().devMode) return;
  started = true;

  ACTIVITY_EVENTS.forEach((evt) =>
    window.addEventListener(evt, onActivity, { passive: true }),
  );

  // Subscribe so we restart timers on sign-in and stop them on sign-out.
  useAuthStore.subscribe((state, prev) => {
    const wasIn = !!prev.user;
    const isIn  = !!state.user;
    if (!wasIn && isIn) scheduleTimers();
    if (wasIn && !isIn) clearTimers();
  });

  // If there is already a user (page reload mid-session), arm immediately.
  if (useAuthStore.getState().user) scheduleTimers();
}

export const _idleConfig = { IDLE_LOGOUT_MS, IDLE_WARN_MS };
